import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { isUndefined as _isUndefined } from 'lodash-es';

export type TParamFilterValue = string | string[] | boolean | undefined;

export type TParamFilters = Record<string, TParamFilterValue>;
/**
 * API UTILS
 *
 * Utils related to communicating with the API
*/

/**
 * Takes an object with the generic type and converts it to http params in a format
 * that is compatible with our restapi
 * @param filters
 */
export const convertObjectToRestapiParams = <T>(
    filters: T
): HttpParams | undefined => {
    if (typeof filters !== 'object' || filters === null) {
        console.warn('Trying to convert something that is not a valid object into restapi params');
        return undefined;
    }

    const filtersObject = filters as Record<string, unknown>;

    // eslint-disable-next-line complexity
    return Object.keys(filtersObject).reduce((params, paramKey) => {
        const paramValue = filtersObject[paramKey];

        if (paramValue === undefined || paramValue === null) {
            return params;
        }

        // handle arrays
        if (Array.isArray(paramValue)) {
            const valueArray = paramValue as unknown[];

            if (valueArray.length > 0) {
                for (const item of valueArray) {
                    // HttpParams.append() expects string or number, but we receive unknown.
                    // we check for valid types before appending.
                    if (item !== undefined && item !== null) {
                        // We must ensure the item is a string/number/boolean which HttpParams can handle.
                        // If it's not a primitive, converting it to a string is the default action.
                        params = params.append(`${paramKey}[]`, `${item}`);
                    }
                }
            }
        } else {
            // handle non arrays
            params = params.append(paramKey, `${paramValue}`);
        }

        return params;
    }, new HttpParams()); // Initial value for reduce is a new HttpParams object
};

/**
 * Receives an HTTPErrorResponse and returns the appropriate error string depending on the structure of the error
 * @param err
 * @returns
 */
export const extractErrorMessageStringFromHttpErrorResponse = (err: HttpErrorResponse): string => {
    if (err.error instanceof ErrorEvent) {
        // client-side network error
        return err.error.message;
    } else if (typeof err.error === 'string') {
        // server responded with a plain string error
        return err.error;
    } else if (err.error?.message) {
        // server responded with a JSON object containing a message property
        return err.error.message;
    } else {
        // fallback to HTTP status and message
        return `${err.status} - ${err.statusText}`;
    }
};

/**
 * Builds URL query string segments for a single filter (name + value).
 * Values are URI-encoded. Arrays are emitted as repeated params with bracket notation (e.g. `name[]=a&name[]=b`).
 *
 * @param filterName - The query parameter name.
 * @param value - The filter value (string, number, array, or undefined).
 * @returns Array of encoded query segments, e.g. `['key=value']` or `['key[]=v1', 'key[]=v2']`. Empty if value is undefined.
 */
const getQueryParamSegments = (filterName: string, value: TParamFilterValue): string[] => {
    if (_isUndefined(value)) {
        return [];
    }
    const encodedName = encodeURIComponent(filterName);
    if (Array.isArray(value)) {
        return value.map(v => `${encodedName}[]=${encodeURIComponent(String(v))}`);
    }
    return [`${encodedName}=${encodeURIComponent(String(value))}`];
};

export const stringifyQueryParams = (filterParams: TParamFilters): string => {
    const segments = (Object.entries(filterParams) as [string, TParamFilterValue][]).flatMap(
        ([filterName, value]) => getQueryParamSegments(filterName, value)
    );
    return segments.length === 0 ? '' : `?${segments.join('&')}`;
};
